/**
 * 従業員情報（v3.0.0 slim統合版）
 *
 * /staff (公開情報) と /employees (内部管理) を /employees に一本化。
 * 履歴書事項 (住所・家族・経歴詳細) を排除し、ID/PASS と必要最小限の運用情報に集約。
 *
 * legacy 互換のため personalInfo / employmentInfo / weeklyShiftTemplate 等は
 * 残してあるが optional。新規UIではフラットなトップレベルフィールドのみ扱う。
 */
export interface Employee {
  id: string;
  shopId: string;
  userId?: string;       // Firebase Auth uid
  employeeCode?: string;
  email?: string;        // ログイン用メール（loginEmail と等価）

  // ロール（権限）— v3.0.0 で 'parttime' を追加して employmentType を廃止
  role?: 'owner' | 'manager' | 'staff' | 'parttime';

  // ===== v3.0.0 slim フィールド（推奨：UIはこれを使用） =====
  // 旧 displayName / personalInfo.firstName+lastName から自動マッピング可
  name?: string;            // 表示名
  kanaName?: string;
  phoneNumber?: string;    // 連絡先（必須相当）
  birthDate?: Date;        // 生年月日
  hireDate?: Date;         // 入社日

  // ログイン制御
  loginEmail?: string;
  loginEnabled?: boolean;  // ログイン停止フラグ（false でログイン拒否）

  // 給与（累進ティア用）
  monthlySalary?: number;  // 月給
  hourlyWage?: number;     // 時給

  // 売上分配オーバーライド
  salesProfitDeductionRate?: number;  // 0-1
  backRate?: number;                  // 0-1

  // --- v4.1.0: NightPro 台帳の統合で持ち込んだ項目 ---
  /** 歩合率（0-1）。売上または分配対象額に掛ける */
  commissionRate?: number;
  /** 歩合の母数。'sales'=売上 / 'base'=分配対象額 / 'none'=歩合なし */
  commissionBase?: 'sales' | 'base' | 'none';
  /** バックを支給する対象か。false なら基本給と歩合のみ */
  backEnabled?: boolean;
  /** NightPro(Laravel) 側のスタッフID。取り込みの二重登録を防ぐ */
  nightProStaffId?: number;

  // 公開プロフィール（旧 /staff 機能 統合）
  photo?: string | null;        // プロフィール写真
  introduction?: string;        // 自己紹介
  showOnHomepage?: boolean;     // ホームページ公開フラグ
  nominationFee?: number;       // 指名料
  displayOrder?: number;        // 公開時の表示順
  isBookableStaff?: boolean;    // 予約システム指名対象

  // ステータス
  isActive?: boolean;       // false で削除扱い（論理削除）
  status?: 'active' | 'on_leave' | 'resigned' | 'deleted';

  // ===== Legacy 互換（既存UI が利用、新規UIでは使用しない） =====
  /** v3.0.0: 段階的廃止予定。新規UIは birthDate/phoneNumber/kanaName をトップレベルへ */
  personalInfo: {
    lastName: string;
    firstName: string;
    lastNameKana: string;
    firstNameKana: string;
    birthDate: Date;
    gender: 'male' | 'female' | 'other' | 'not_specified';
    phone: string;
    emergencyContact?: { name: string; relationship: string; phone: string };
    address?: { postalCode: string; prefecture: string; city: string; street: string; building?: string };
  };

  /** v3.0.0: 段階的廃止予定。新規UIは role/monthlySalary/hourlyWage をトップレベルへ */
  employmentInfo: {
    employmentType: 'full_time' | 'part_time' | 'contract' | 'dispatch' | 'owner_family' | 'other';
    position: string;
    department?: string;
    hireDate?: Date;
    isStudent?: boolean;
    schoolName?: string;
    grade?: string;
    graduationDate?: Date;
    paymentType: 'hourly' | 'monthly' | 'daily' | 'none';
    hourlyWage?: number;
    monthlyWage?: number;
    dailyWage?: number;
    transportationAllowanceType?: 'per_day' | 'monthly_fixed';
    transportationAllowance?: number;
    nightShiftRate?: number;
    workingDaysPreference?: string[];
  };

  // LINE連携（残置）
  lineUserId?: string;
  lineNotificationEnabled?: boolean;

  // GPS勤怠
  gpsAttendance?: {
    enabled: boolean;
    allowedRadius?: number;
  };

  // 通知設定
  notificationSettings?: {
    shiftApproved?: boolean;
    shiftChanged?: boolean;
    shiftRequest?: boolean;
    shiftReminder?: boolean;
    shiftRequestResult?: boolean;
    attendanceApproved?: boolean;
    salaryCalculated?: boolean;
    reminderTiming?: 'day_before' | 'day_before_and_morning';
    orderNotificationCategories?: string[];
    callNotificationEnabled?: boolean;
  };

  /** @deprecated v3.0.0 で削除予定 */
  skills?: { skillName: string; level?: string; certificationDate?: Date }[];

  /** @deprecated v3.0.0 で削除予定 */
  weeklyShiftTemplate?: any;

  /** @deprecated v3.0.0 で削除予定（loginEmail に統合） */
  displayName?: string;
  /** @deprecated v3.0.0 で削除予定 */
  profileImage?: string;
  /** @deprecated v3.0.0 で削除予定 */
  description?: string;

  currentWorkStatus?: {
    isWorking: boolean;
    currentAttendanceId?: string;
    clockInTime?: Date;
    location?: { latitude: number; longitude: number };
    isTestMode?: boolean;
  };

  deletedAt?: Date;
  deletedBy?: string;

  createdAt: Date;
  updatedAt: Date;
  createdBy?: string;
}

/**
 * ロールの日本語表示（v3.0.0: parttime 追加）
 */
export const roleLabels: Record<NonNullable<Employee['role']>, string> = {
  owner: 'オーナー',
  manager: 'マネージャー',
  staff: '社員',
  parttime: 'アルバイト',
};

/**
 * ステータスの日本語表示
 */
export const statusLabels: Record<NonNullable<Employee['status']>, string> = {
  active: '在籍中',
  on_leave: '休職中',
  resigned: '退職',
  deleted: '削除済み',
};

/** @deprecated v3.0.0 で role に統合済 */
export const employmentTypeLabels = {
  owner_family: 'オーナー・家族',
  full_time: '正社員',
  part_time: 'パート・アルバイト',
  contract: '契約社員',
  dispatch: '派遣社員',
  other: 'その他',
} as const;

/** @deprecated v3.0.0 で paymentType を role と給与フィールドで判定 */
export const paymentTypeLabels = {
  none: 'なし（オーナー等）',
  hourly: '時給',
  monthly: '月給',
  daily: '日給',
} as const;

/** @deprecated v3.0.0 で性別フィールド廃止 */
export const genderLabels = {
  male: '男性',
  female: '女性',
  other: 'その他',
  not_specified: '未指定',
} as const;
